import React, { useState } from 'react';
import { EvvPatientMapResult, EvvReconciliationService } from '../../../../services/evv/reconciliation.service';
import { errorMessage } from './evvUi';

/**
 * EVV-15 / checklist step 7: import HHAX patient IDs from the HHAX patient demographics export.
 * The file is matched on Medicaid ID and is not stored.
 */
const EvvPatientMapPanel: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<EvvPatientMapResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const importFile = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await EvvReconciliationService.importPatientMap(file, { overwrite }));
      setFile(null);
    } catch (err) {
      setError(errorMessage(err, 'The import failed'));
    } finally {
      setBusy(false);
    }
  };

  const rows = (list: number[]) => (list.length > 10 ? `${list.slice(0, 10).join(', ')}…` : list.join(', '));

  return (
    <div className="rounded-md border border-slate-200 p-3">
      <p className="text-sm font-medium text-slate-700">HHAX patient IDs</p>
      <p className="mt-1 text-xs text-slate-500">
        Upload the HHAX Patient Demographics Export (HHAX sends it by SFTP on request; it needs the Patient ID and Medicaid Number columns). Each HHAX
        patient ID is saved on the Pelora patient with the same Medicaid ID, so exports match even when
        User Field 1 is blank. The file itself is not stored.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <input type="file" accept=".csv,text/csv" data-testid="evv-patient-map-input" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" />
        <label className="text-xs text-slate-600">
          <input type="checkbox" className="mr-1" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
          Replace HHAX IDs that differ
        </label>
        <button
          type="button"
          onClick={importFile}
          disabled={!file || busy}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {busy ? 'Importing…' : 'Import'}
        </button>
      </div>
      {error && <p className="mt-2 text-xs text-rose-700" role="alert">{error}</p>}
      {result && (
        <div className="mt-2 text-xs text-slate-700" data-testid="evv-patient-map-result">
          <p>{result.mapped} mapped · {result.unchanged} already mapped · {result.rowCount} rows</p>
          {result.unmatchedRows.length > 0 && <p className="text-amber-700">No Pelora patient with that Medicaid ID: rows {rows(result.unmatchedRows)}</p>}
          {result.ambiguousRows.length > 0 && <p className="text-amber-700">More than one Pelora patient has that Medicaid ID: rows {rows(result.ambiguousRows)}</p>}
          {result.conflicts.length > 0 && <p className="text-amber-700">Conflicts (not changed): rows {rows(result.conflicts.map((c) => c.rowNumber))}</p>}
          {result.invalidRows.length > 0 && <p className="text-amber-700">Missing Patient ID or Medicaid Number: rows {rows(result.invalidRows)}</p>}
        </div>
      )}
    </div>
  );
};

export default EvvPatientMapPanel;
